import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation, User } from '../types';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import { formatConversationTime } from '../utils/formatters';
import { ConnectionService } from '../services/connectionService';
import { ROUTES } from '../constants/app';

interface ConversationsListProps {
  conversations: Conversation[];
  currentUser: User;
}

export const ConversationsList: React.FC<ConversationsListProps> = ({
  conversations,
  currentUser
}) => {
  const navigate = useNavigate();

  const handleSelectConversation = (conversation: Conversation) => {
    navigate(`/chat/${conversation.id}`);
  };

  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  // Validar que conversations sea un array válido
  const validConversations = Array.isArray(conversations) ? conversations : [];

  return (
    <div className="flex flex-col w-full h-full bg-white">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Conversations</h1>
        </div>
        <div className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-full">
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">{validConversations.length}</span>
        </div>
      </header>

      {/* Conversations List */}
      <main className="flex-1 overflow-y-auto">
        {validConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
            <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">No hay conversaciones aún</h3>
            <p className="text-gray-500">¡Comienza a hacer swipe para crear conexiones y empezar conversaciones!</p>
            <p className="text-sm text-gray-400 mt-2">
              Nota: El sistema de chat se implementará próximamente con datos de Supabase
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {validConversations
              .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
              .map((conversation) => {
                try {
                  // Validar estructura de conversación más robustamente
                  if (!conversation || !conversation.id || !conversation.participants) {
                    console.warn('⚠️ Conversación con estructura inválida:', conversation);
                    return null;
                  }
                  
                  // Manejo seguro para obtener el otro usuario
                  let otherUser: User | null = null;
                  
                  try {
                    // Crear usuario básico desde IDs de la conversación
                    const otherUserId = (conversation as any).participant1_id === currentUser.id 
                      ? (conversation as any).participant2_id 
                      : (conversation as any).participant1_id;
                    
                    if (otherUserId) {
                      // Crear usuario básico temporal
                      otherUser = {
                        id: otherUserId,
                        name: 'Usuario',
                        age: 30,
                        location: 'Ubicación',
                        userType: 'entrepreneur',
                        images: ['https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop']
                      } as User;
                    }
                  } catch (error) {
                    console.warn('⚠️ Error al obtener otro usuario:', error);
                    // Crear usuario por defecto si hay error
                    otherUser = {
                      id: 'unknown-user',
                      name: 'Usuario Desconocido',
                      age: 30,
                      location: 'Ubicación',
                      userType: 'entrepreneur',
                      images: ['https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop']
                    } as User;
                  }
                  
                  // Asegurar que images existe y tiene al menos una imagen
                  const userImages = otherUser.images && Array.isArray(otherUser.images) && otherUser.images.length > 0
                    ? otherUser.images
                    : ['https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop'];
                  
                  // Verificar mensajes no leídos de forma segura
                  const unread = false; // Por ahora, sin indicador de no leídos
                
                return (
                  <div
                    key={conversation.id}
                    onClick={() => handleSelectConversation(conversation)}
                    className="flex items-center p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                  >
                    {/* Profile Image */}
                    <div className="relative">
                      <img
                        src={userImages[0]}
                        alt={otherUser.name}
                        className="w-14 h-14 rounded-full object-cover object-center"
                        onError={(e) => {
                          // Fallback image si la imagen no carga
                          (e.target as HTMLImageElement).src = 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop';
                        }}
                      />
                      {unread && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full border-2 border-white" />
                      )}
                    </div>

                    {/* Conversation Info */}
                    <div className="flex-1 ml-4 min-w-0 space-y-1">
                      <div className="flex items-center justify-between">
                        <h3 className={`font-semibold truncate ${unread ? 'text-gray-900' : 'text-gray-700'}`}>
                          {otherUser.name}
                        </h3>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatConversationTime(conversation.updatedAt)}
                        </span>
                      </div>
                      
                      <div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          otherUser.userType === 'entrepreneur' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {otherUser.userType === 'entrepreneur' ? 'Entrepreneur' : 'Investor'}
                        </span>
                      </div>

                      {conversation.lastMessage && (
                        <p className={`text-sm truncate ${
                          unread ? 'text-gray-900 font-medium' : 'text-gray-500'
                        }`}>
                          {conversation.lastMessage.senderId === currentUser.id ? 'You: ' : ''}
                          {conversation.lastMessage.content}
                        </p>
                      )}
                    </div>
                  </div>
                );
                } catch (error) {
                  console.error('❌ Error al renderizar conversación:', conversation?.id || 'ID desconocido', error);
                  return null;
                }
              })
              .filter(item => item !== null)} {/* Filtrar elementos null */}
          </div>
        )}
      </main>
    </div>
  );
};